import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowUpRight, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { AppShell } from '../components/Layout';
import { UsageMeter } from '../components/billing/UsageMeter';
import { billingApi } from '../lib/billing';
import { getErrorMessage } from '../lib/errors';
import { toast } from 'sonner';

const STATUS_BADGE = {
  active: { variant: 'default', label: 'ACTIVE' },
  past_due: { variant: 'secondary', label: 'PAST DUE' },
  canceled: { variant: 'outline', label: 'CANCELED' },
  expired: { variant: 'destructive', label: 'EXPIRED' },
  none: { variant: 'outline', label: 'NO PLAN' },
};

export default function BillingPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const refresh = async () => {
    try {
      const d = await billingApi.me();
      setMe(d);
    } catch (_e) { /* keep prior state */ }
  };

  const onConfirmCancel = async () => {
    setCancelling(true);
    try {
      await billingApi.cancelSubscription({ cancel_at_cycle_end: true });
      toast.success(
        me?.plan_type === 'gold'
          ? 'Subscription cancelled. Access continues until the end of your current cycle.'
          : 'Plan cancelled. Existing audits remain usable until expiry.',
      );
      setCancelOpen(false);
      window.dispatchEvent(new Event('billing:refresh'));
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Cancel failed. Try again or contact support.'));
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const d = await billingApi.me();
        if (active) setMe(d);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    const onRefresh = () => load();
    window.addEventListener('billing:refresh', onRefresh);
    return () => {
      active = false;
      window.removeEventListener('billing:refresh', onRefresh);
    };
  }, []);

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 flex items-center justify-center min-h-[40vh]">
          <Loader2 size={20} className="animate-spin text-primary" />
        </div>
      </AppShell>
    );
  }

  if (!me) {
    return (
      <AppShell>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 text-center">
          <p className="text-muted-foreground">Could not load billing info.</p>
        </div>
      </AppShell>
    );
  }

  const badge = STATUS_BADGE[me.status] || STATUS_BADGE.none;
  const usage = me.usage || {};
  const isComp = me.comp;
  const isNoPlan = me.entitlement_key === 'none' || me.entitlement_key === 'expired';

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <header data-testid="billing-header">
          <p className="font-mono-edge text-xs tracking-[0.18em] uppercase text-muted-foreground">BILLING SETTINGS</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.01em] mt-1">Billing</h1>
          <p className="text-sm text-muted-foreground mt-1">Plan status, quota usage, and payments.</p>
        </header>

        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          {/* Plan summary + usage */}
          <div className="space-y-6">
            <Card data-testid="billing-plan-summary-card">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-mono-edge text-[10px] tracking-[0.18em] uppercase text-muted-foreground">CURRENT PLAN</p>
                    <h2 className="font-display text-2xl mt-0.5" data-testid="billing-plan-label">{me.label}</h2>
                    {isComp && (
                      <p className="text-xs text-muted-foreground mt-1">Complimentary access &mdash; your account was granted at launch.</p>
                    )}
                  </div>
                  <Badge variant={badge.variant} className="font-mono-edge" data-testid="billing-status-badge">{badge.label}</Badge>
                </div>

                {typeof me.days_remaining === 'number' && me.current_period_end && (
                  <div className="flex items-center gap-3">
                    <span className="font-mono-edge text-xs px-2 py-1 rounded-md border border-border bg-secondary" data-testid="billing-days-remaining">
                      {me.days_remaining}d left
                    </span>
                    <p className="text-xs text-muted-foreground">Period ends {new Date(me.current_period_end).toLocaleDateString()}</p>
                  </div>
                )}

                <Separator />

                <div className="space-y-4">
                  <UsageMeter
                    label="Audits"
                    used={usage.audits_used || 0}
                    limit={me.audits_limit}
                    testId="billing-usage-audits"
                  />
                  <UsageMeter
                    label="Posts (Researched + Prompt)"
                    used={usage.posts_used || 0}
                    limit={me.posts_limit}
                    testId="billing-usage-posts"
                  />
                  <UsageMeter
                    label="Carousels"
                    used={usage.carousels_used || 0}
                    limit={me.carousels_limit}
                    testId="billing-usage-carousels"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Payments table (empty state for now) */}
            <Card data-testid="billing-payments-table">
              <CardContent className="p-6">
                <p className="font-mono-edge text-[10px] tracking-[0.18em] uppercase text-muted-foreground">RECENT PAYMENTS</p>
                <h3 className="font-display text-lg mt-1">Invoices</h3>
                <div className="mt-6 py-10 text-center text-sm text-muted-foreground" data-testid="billing-payments-empty">
                  No payments yet. Your recent payments will appear here.
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Actions */}
          <div className="space-y-4">
            <Card>
              <CardContent className="p-6 space-y-3">
                <p className="font-mono-edge text-[10px] tracking-[0.18em] uppercase text-muted-foreground">ACTIONS</p>
                {isNoPlan ? (
                  <Button className="w-full" onClick={() => navigate('/pricing')} data-testid="billing-upgrade-button">
                    Choose a plan <ArrowUpRight size={14} className="ml-1" />
                  </Button>
                ) : (
                  <>
                    {me.plan_type === 'gold' && !isComp && (
                      <>
                        <Button className="w-full" variant="default" onClick={() => navigate('/pricing')} data-testid="billing-manage-subscription-button">
                          Manage subscription
                        </Button>
                        <Button
                          className="w-full"
                          variant="outline"
                          onClick={() => setCancelOpen(true)}
                          disabled={me.status === 'canceled'}
                          data-testid="billing-cancel-subscription-button"
                        >
                          <XCircle size={14} className="mr-1.5" />
                          {me.status === 'canceled' ? 'Cancellation pending' : 'Cancel subscription'}
                        </Button>
                      </>
                    )}
                    {me.plan_type === 'one_time' && !isComp && (
                      <>
                        <Button className="w-full" variant="secondary" disabled={me.days_remaining > 0} onClick={() => navigate('/pricing')} data-testid="billing-buy-again-button">
                          {me.days_remaining > 0 ? `Renew after ${me.days_remaining}d` : 'Buy again'}
                        </Button>
                        {me.status === 'active' && (
                          <Button
                            className="w-full"
                            variant="outline"
                            onClick={() => setCancelOpen(true)}
                            data-testid="billing-cancel-onetime-button"
                          >
                            <XCircle size={14} className="mr-1.5" />
                            Cancel plan
                          </Button>
                        )}
                      </>
                    )}
                  </>
                )}
                <a href="/refund-policy" className="block text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline mt-2" data-testid="billing-refund-link">
                  Refund policy
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <AlertDialog open={cancelOpen} onOpenChange={(o) => !cancelling && setCancelOpen(o)}>
        <AlertDialogContent data-testid="billing-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-[hsl(var(--warning))]" />
              {me?.plan_type === 'gold' ? 'Cancel your subscription?' : 'Cancel your plan?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 pt-1">
              {me?.plan_type === 'gold' ? (
                <>
                  Your Executive Thought Leadership Plan stays active through the end of the
                  current paid cycle{me?.days_remaining ? ` (about ${me.days_remaining} more day${me.days_remaining === 1 ? '' : 's'})` : ''}.
                  After that, posts, carousels and audits will pause and we won&apos;t charge you again.
                </>
              ) : (
                <>
                  Your Profile Success Plan will be marked cancelled. Any audits and posts you&apos;ve
                  already created stay in your account. You won&apos;t be charged again &mdash; this is a
                  one-time purchase.
                </>
              )}
              <span className="block text-xs mt-2">
                Changed your mind?{' '}
                <a href="/refund-policy" className="underline underline-offset-4 hover:text-foreground">
                  Read the 24-hour refund policy
                </a>
                {' '}or email{' '}
                <a href="mailto:hi@dotconverse.com" className="underline underline-offset-4 hover:text-foreground">
                  hi@dotconverse.com
                </a>.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={cancelling}
              data-testid="billing-cancel-dialog-keep"
            >
              Keep my plan
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmCancel}
              disabled={cancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="billing-cancel-dialog-confirm"
            >
              {cancelling ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Cancelling&hellip;</>
              ) : (
                'Yes, cancel'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
